import { useState, useEffect, useRef } from "react"
import { DataSet, Network } from "vis-network/standalone/esm/vis-network"
import { useApiService } from "../hooks/Api/useApiService"
import T from "../hooks/Language/T"
import { Box, debounce, Paper, Typography } from "@mui/material"
import { useNavigate } from "react-router-dom"
import ViewSkillModal from "../Modals/ViewSkillModal/ViewSkillModal"
import useTranslationSharie from "../hooks/Language/useTranslation"
import NodeSkillsGroups from "./NodeSkillsGroups"

const LinkNetwork = ({ user_name }) => {
  const [groups, setGroups] = useState([])
  const [skills, setSkills] = useState([])
  const [selected_skill, setSelelectedSkill] = useState(null)
  const [followers, setFollowers] = useState([])
  const [following, setFollowing] = useState([])
  const { postData } = useApiService()
  const navigate = useNavigate()
  const viewRef = useRef()
  const t = useTranslationSharie()
  const networkRef = useRef(null)
  const nodesRef = useRef(new DataSet())
  const edgesRef = useRef(new DataSet())
  const isMobileDevice = /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
  const addNodeIfNotExists = (id, label, image) => {
    if (!nodesRef.current.get(id)) {
      nodesRef.current.add({ id, label, shape: "circularImage", size: 30, image })
    }
  }

  const addEdgeIfNotExists = (from, to) => {
    const exists =
      edgesRef.current.get({
        filter: (item) => item.from === from && item.to === to,
      }).length > 0

    if (!exists) {
      try {
        edgesRef.current.add({ from, to, arrows: "to" })
      } catch (err) {}
    }
  }

  useEffect(() => {
    postData("sharie/getfollowers", { user_name })
      .then((data) => {
        setFollowers(data?.followers || [])
        return postData("sharie/getfollowing", { user_name })
      })
      .then((data) => {
        setFollowing(data?.following || [])
      })
  }, [user_name])

  useEffect(() => {
    if (!user_name) return

    const nodes = nodesRef.current
    const edges = edgesRef.current

    nodes.clear()
    edges.clear()

    nodes.add({
      id: user_name,
      label: localStorage.getItem("user") === user_name ? t("Me") : user_name,
      shape: "dot",
      size: 30,
      color: "#1976d2",
    })

    followers.forEach(({ user_name: follower, image }) => {
      addNodeIfNotExists(follower, follower, image)
      addEdgeIfNotExists(follower, user_name)
    })

    following.forEach(({ user_name: followed, image }) => {
      addNodeIfNotExists(followed, followed, image)
      addEdgeIfNotExists(user_name, followed)
    })

    const container = document.getElementById("network")
    const network = new Network(
      container,
      { nodes, edges },
      {
        nodes: { font: { size: 14 } },
        edges: {
          arrows: { to: { enabled: true, scaleFactor: 0.5 } },
          color: { inherit: true },
        },
        physics: {
          enabled: true,
          forceAtlas2Based: {
            gravitationalConstant: -26.5,
            centralGravity: 0.01,
            springLength: 100,
          },
          solver: "forceAtlas2Based",
          timestep: 0.5,
        },
        // physics: {
        //   enabled: true,
        //   solver: "barnesHut", // Using barnesHut for better performance and stability
        //   barnesHut: {
        //     gravitationalConstant: -1000, // Adjusted for more controlled node repulsion
        //     centralGravity: 0.3, // Stronger pull to the center
        //     springLength: 100, // Keeps nodes a reasonable distance from each other
        //     springConstant: 0.1, // Slightly weaker springs for less aggressive movement
        //   },
        //   stabilization: {
        //     iterations: 200, // Increase the number of iterations for better stabilization
        //     updateInterval: 25, // Shorter interval for quicker stabilization
        //   },
        //   timestep: 0.5, // A reasonable time step for steady animations
        // }
        interaction: {
          hover: true,
          tooltipDelay: 200,
        },
      }
    )
    // network.once("stabilizationIterationsDone", () => {
    //   network.setOptions({ physics: false })
    // })

    network.on("click", isMobileDevice ? handleNodeTapMobile : handleNodeClick)
    network.on("hoverNode", handleNodeMouseover)

    networkRef.current = network
  }, [followers, following, user_name])

  const handleNodeClick = (event) => {
    const { nodes } = event
    if (nodes.length > 0) {
      const clickedNodeId = nodes[0]
      navigate("/profile", {
        state: {
          user_name: clickedNodeId,
          editable: localStorage.getItem("user") === clickedNodeId,
        },
      })
    }
  }
  const handleNodeTapMobile = async (event) => {
    const nodeId = event.node
    if (!nodeId) return
    fetchSkills(nodeId)

    const node = nodesRef.current.get(nodeId)
    if (node?.fetched) return

    try {
      const followersRes = await postData("shaire/getfollowers", { user_name: nodeId })
      const followingRes = await postData("sharie/getfollowing", { user_name: nodeId })

      const newFollowers = followersRes?.followers || []
      const newFollowing = followingRes?.following || []

      newFollowers.forEach(({ user_name: follower, image }) => {
        addNodeIfNotExists(follower, follower, image)
        addEdgeIfNotExists(follower, nodeId)
      })

      newFollowing.forEach(({ user_name: followed, image }) => {
        addNodeIfNotExists(followed, followed, image)
        addEdgeIfNotExists(nodeId, followed)
      })
      nodesRef.current.update({ id: nodeId, fetched: true })

      if (networkRef.current) {
        networkRef.current.setData({
          nodes: nodesRef.current,
          edges: edgesRef.current,
        })
      }
    } catch (err) {}
  }
  const edgesExist = (nodeId) => {
    const exists_a =
      edgesRef.current.get({
        filter: (item) => item.from === user_name && item.to === nodeId,
      }).length > 0
    const exists_b =
      edgesRef.current.get({
        filter: (item) => item.from === nodeId && item.to === user_name,
      }).length > 0

    return exists_a && exists_b
  }

  const handleNodeMouseover = debounce(async (event) => {
    const nodeId = event.node
    if (!nodeId) return
    fetchSkills(nodeId)
    if (!edgesExist(nodeId)) {
      return
    }
    const node = nodesRef.current.get(nodeId)
    if (node?.fetched) return

    try {
      const followersRes = await postData("sharie/getfollowers", { user_name: nodeId })
      const followingRes = await postData("sharie/getfollowing", { user_name: nodeId })

      const newFollowers = followersRes?.followers || []
      const newFollowing = followingRes?.following || []

      newFollowers.forEach(({ user_name: follower, image }) => {
        addNodeIfNotExists(follower, follower, image)
        addEdgeIfNotExists(follower, nodeId)
      })

      newFollowing.forEach(({ user_name: followed, image }) => {
        addNodeIfNotExists(followed, followed, image)
        addEdgeIfNotExists(nodeId, followed)
      })

      nodesRef.current.update({ id: nodeId, fetched: true })

      if (networkRef.current) {
        networkRef.current.setData({
          nodes: nodesRef.current,
          edges: edgesRef.current,
        })
      }
    } catch (err) {}
  }, 300)

  const fetchSkills = (userId) => {
    postData("skills/getskillsbyuser", { user_name: userId })
      .then((data) => {
        setSkills(data?.skills || [])
        return postData("groups/getgroups", { user_name: userId })
      })
      .then((data) => {
        setGroups(data?.groups || [])
      })
  }

  const handleSkillGroupClick = (skill) => {
    postData("sharie/addnotification", {
      skill_name: skill?.name,
      from: localStorage.getItem("user"),
      type: "view " + (skill.max_participants ? "group" : "skill"),
    })

    if (skill.max_participants) {
      navigate("/groups", { state: { skill } })
    } else {
      setSelelectedSkill(skill)
      if (viewRef.current) viewRef.current.childFunction()
    }
  }

  return (
    <T>
      <ViewSkillModal ref={viewRef} skill={selected_skill} />

      <Paper
        elevation={4}
        sx={{
          background: "transparent",
          borderRadius: "0.8rem",
          p: 2,
          m: 2,
        }}
      >
        <Typography variant='h5' gutterBottom>
          Links Map
        </Typography>

        <Box
          id='network'
          sx={{
            width: "100%",
            height: "400px",
            borderRadius: "0.3rem",
          }}
        />

        <NodeSkillsGroups
          skills={skills}
          groups={groups}
          handleSkillGroupClick={handleSkillGroupClick}
          t={t}
        />
      </Paper>
    </T>
  )
}

export default LinkNetwork
